import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import oceanMain from "../../assets/ocean_main.webp";
import autumnWalk from "../../assets/tree-walk.webp";

export function AboutHero() {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroOpacity = useTransform(heroScroll, [0, 0.5], [1, 0]);
  const heroScale = useTransform(heroScroll, [0, 0.5], [1, 1.1]);

  return (
    <div className="w-screen overflow-hidden min-h-screen bg-white">
      {/* Hero Section - Full Screen */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        <motion.div
          className="absolute inset-0"
          style={{ opacity: heroOpacity, scale: heroScale }}
        >
          <div className="absolute inset-0">
            <img
              src={oceanMain}
              className="absolute top-0 left-0 w-full h-full object-cover"
              alt="Ocean Hero Background"
            />
            <div className="absolute inset-0 bg-black/40" />
          </div>
        </motion.div>

        <div className="relative h-full flex items-center justify-center px-6">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <h1
                className="mb-6"
                style={{
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: "clamp(3rem, 8vw, 7rem)",
                  fontWeight: "900",
                  color: "white",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  lineHeight: "1",
                }}
              >
                My Journey
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10">to God</span>
                  <motion.span
                    className="absolute bottom-0 left-0 right-0 h-4 lg:h-8"
                    style={{ backgroundColor: "#E63946", opacity: 0.5 }}
                    initial={{ scaleX: 0, transformOrigin: "left" }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.2, delay: 1.2 }}
                  />
                </span>
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="relative bg-white py-32 lg:py-48 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8">
              <span
                style={{
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: "0.875rem",
                  fontWeight: "700",
                  color: "#00E5FF",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                }}
              >
                Introduction
              </span>
            </div>
            <h2
              className="mb-12"
              style={{
                fontFamily: "League Spartan, sans-serif",
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: "900",
                color: "#0A0A0A",
                letterSpacing: "-0.01em",
                lineHeight: "1.1",
                textTransform: "uppercase",
              }}
            >
              30 years in 30 seconds
            </h2>
            <div className="space-y-6">
              <p className="text-xl text-[#222] leading-relaxed">
                I grew up at a church, volunteering three nights a week, always
                involved, always helping… until I wasn’t. Somewhere along the
                line, I turned my back.
              </p>
              <p className="text-xl text-[#222] leading-relaxed">
                My early twenties were the hardest years of my life — trying to
                find my feet in a world full of temptations, noise, and
                distractions. I felt completely lost. I blamed God for my
                circumstances. I got angry because He wasn’t answering my
                prayers. I thought He’d left me… so I left Him.
              </p>
              <p className="text-xl text-[#222] leading-relaxed">
                Then everything changed. I met my amazing wife, and we started a
                family. She quietly prayed that I’d find my way back to God,
                even when I was stubborn, closed off, and convinced I didn’t
                need Him. But now I can see it clearly — God was working the
                entire time.
              </p>
              <p className="text-xl text-[#222] leading-relaxed">
                He showed me grace I didn’t deserve. He forgave me long before I
                even asked. He blessed me with a home, a career, and a family
                that reflects His goodness. I thought I was doing life on my
                own… but He was the one carrying me through it all.
              </p>
              <p className="text-xl text-[#222] leading-relaxed font-semibold">
                And then He spoke — through the number 7. Again and again. Until
                I finally listened.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Turning Point - Full Width Image */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={autumnWalk}
            alt="Autumn walk"
            className="w-full h-full object-cover contrast-125"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        </div>

        <div className="relative h-full flex items-center justify-center px-6">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="mb-8"
              style={{
                fontFamily: "League Spartan, sans-serif",
                fontSize: "clamp(2.5rem, 6vw, 5rem)",
                fontWeight: "900",
                color: "white",
                letterSpacing: "0.02em",
                lineHeight: "1.1",
                textTransform: "uppercase",
              }}
            >
              The Turning Point
            </h2>
            <p
              style={{
                fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
                color: "rgba(255,255,255,0.95)",
                lineHeight: "1.7",
                fontStyle: "italic",
              }}
            >
              "I finally listened. I listened to my wife. I listened to an
              album. Most importantly, I listened to God."
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Impact */}
      <section className="relative bg-white py-32 lg:py-48 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8">
              <span
                style={{
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: "0.875rem",
                  fontWeight: "700",
                  color: "#E63946",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                }}
              >
                The Impact
              </span>
            </div>
            <h2
              className="mb-12"
              style={{
                fontFamily: "League Spartan, sans-serif",
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: "900",
                color: "#0A0A0A",
                letterSpacing: "-0.01em",
                lineHeight: "1.1",
                textTransform: "uppercase",
              }}
            >
              Living
              <br />
              Fully Alive
            </h2>
            <div className="space-y-8">
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
                  color: "#222",
                  lineHeight: "1.7",
                }}
              >
                Those who know me know I go all in — and this time, it’s no
                different. When God woke something in me, He didn’t just change
                what I believed; He changed how I lived. I started opening my
                heart, leading my home with purpose, and raising my son with
                intention. And now I’m fighting to build something bigger — a
                community where faith is lived out every day, not just spoken
                about on Sundays.
              </p>
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
                  color: "#222",
                  lineHeight: "1.7",
                }}
                className=""
              >
                I'm{" "}
                <span style={{ color: "#00E5FF", fontWeight: "600" }}>
                  fully alive in Christ
                </span>{" "}
                — not because I was trying harder, but because I finally stepped
                into the life God has set out for me.
              </p>
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
                  color: "#222",
                  lineHeight: "1.7",
                }}
              >
                And I realised: this is for anyone who wants it. God can
                transform any life that’s willing to open the door.
              </p>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="text-center">
              <div
                className="mb-4"
                style={{
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: "clamp(3rem, 6vw, 5rem)",
                  fontWeight: "900",
                  color: "#E63946",
                  lineHeight: "1",
                }}
              >
                7
              </div>
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "1rem",
                  color: "#444",
                  lineHeight: "1.5",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Themes
              </p>
            </div>
            <div className="text-center">
              <div
                className="mb-4"
                style={{
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: "clamp(3rem, 6vw, 5rem)",
                  fontWeight: "900",
                  color: "#00E5FF",
                  lineHeight: "1",
                }}
              >
                ∞
              </div>
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "1rem",
                  color: "#444",
                  lineHeight: "1.5",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Potential
              </p>
            </div>
            <div className="text-center">
              <div
                className="mb-4"
                style={{
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: "clamp(3rem, 6vw, 5rem)",
                  fontWeight: "900",
                  color: "#0A0A0A",
                  lineHeight: "1",
                }}
              >
                1
              </div>
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "1rem",
                  color: "#444",
                  lineHeight: "1.5",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Purpose
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Mission - Full Width */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1594576396046-503121b82768?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYW4lMjB3YWxraW5nJTIwcGF0aCUyMGpvdXJuZXl8ZW58MXx8fHwxNzYyODAwOTczfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Journey path"
            className="w-full h-full object-cover grayscale contrast-125"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        </div>

        <div className="relative h-full flex items-center justify-center px-6">
          <motion.div
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8">
              <span
                style={{
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: "0.875rem",
                  fontWeight: "700",
                  color: "#00E5FF",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                }}
              >
                My Mission
              </span>
            </div>
            <h2
              className="mb-12"
              style={{
                fontFamily: "League Spartan, sans-serif",
                fontSize: "clamp(3rem, 7vw, 6rem)",
                fontWeight: "900",
                color: "white",
                letterSpacing: "0.02em",
                lineHeight: "1.1",
                textTransform: "uppercase",
              }}
            >
              That's Why I<br />
              <span className="relative inline-block">
                <span className="relative z-10">Built Se7ens</span>
                <motion.span
                  className="absolute bottom-0 left-0 right-0 h-4 lg:h-8"
                  style={{ backgroundColor: "#E63946", opacity: 0.5 }}
                  initial={{ scaleX: 0, transformOrigin: "left" }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </span>
            </h2>
            <p
              className="mb-12"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
                color: "rgba(255,255,255,0.95)",
                lineHeight: "1.7",
                maxWidth: "48rem",
                margin: "0 auto 3rem",
              }}
            >
              Because if God can transform my life, He can transform yours too.
              Open your heart. Open your life. Give Him one moment — and watch
              everything change.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="relative bg-white py-32 lg:py-48 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p
              className="mb-12"
              style={{
                fontFamily: "League Spartan, sans-serif",
                fontSize: "clamp(2rem, 4vw, 3.5rem)",
                fontWeight: "900",
                color: "#0A0A0A",
                letterSpacing: "-0.01em",
                lineHeight: "1.2",
                textTransform: "uppercase",
              }}
            >
              This is my story.
              <br />
              <span className="relative inline-block">
                <span className="relative z-10" style={{ color: "#E63946" }}>
                  What will yours be?
                </span>
                <motion.span
                  className="absolute bottom-0 left-0 right-0 h-3 lg:h-4"
                  style={{ backgroundColor: "#00E5FF", opacity: 0.3 }}
                  initial={{ scaleX: 0, transformOrigin: "left" }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </span>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default AboutHero;
